// Static, frontend-owned problem-report topic taxonomy (single source of truth — ADR-0011).
// No backend endpoint: the user picks a topic and the dialog submits its ready-to-use Linear
// label text as `topicLabel`. The DB-backed list + GET endpoint is deferred to VER-207.

export interface ProblemReportTopic {
  // Stable internal id — never sent to the API; used as the combobox item key.
  key: string;
  // pt-BR display label shown to the user in the combobox.
  label: string;
  // Ready-to-use Linear label text submitted as `topicLabel` (title-case, mirrors `Customer Report`).
  linearLabel: string;
}

// "Outro problema" is pinned last regardless of alphabetical order — it is the catch-all.
const OTHER_KEY = 'other';

// Source taxonomy (VER-207 seed). Declaration order is irrelevant: the exported list is derived
// sorted, so entries are kept here grouped by key for readability.
const TAXONOMY: readonly ProblemReportTopic[] = [
  { key: 'ui-bug', label: 'Algo parece quebrado (erro visual)', linearLabel: 'UI Bug' },
  { key: 'wrong-category', label: 'Categoria errada', linearLabel: 'Wrong Category' },
  { key: 'stale-data', label: 'Conta parou de sincronizar / dados desatualizados', linearLabel: 'Stale Data' },
  { key: 'cant-connect', label: 'Não consigo conectar uma conta', linearLabel: "Can't Connect Account" },
  { key: 'link-bad-answer', label: 'O Link deu uma resposta ruim', linearLabel: 'Link Bad Answer' },
  { key: 'budget-wrong', label: 'Orçamento / limites errados', linearLabel: 'Budget Issue' },
  { key: 'consent-expired', label: 'Preciso reautorizar (consentimento expirou)', linearLabel: 'Consent Expired' },
  { key: 'billing', label: 'Problema de cobrança ou assinatura', linearLabel: 'Billing' },
  { key: 'missing-transactions', label: 'Transações faltando ou duplicadas', linearLabel: 'Missing Transactions' },
  { key: 'wrong-amounts', label: 'Valores ou saldos errados', linearLabel: 'Wrong Amounts' },
  { key: OTHER_KEY, label: 'Outro problema', linearLabel: 'Other' },
];

// Alphabetical by pt-BR display label (locale-aware so accented letters sort correctly),
// with the catch-all "Outro" forced last.
function byDisplayLabelOtherLast(a: ProblemReportTopic, b: ProblemReportTopic): number {
  if (a.key === OTHER_KEY) return 1;
  if (b.key === OTHER_KEY) return -1;
  return a.label.localeCompare(b.label, 'pt-BR');
}

export const PROBLEM_REPORT_TOPICS: readonly ProblemReportTopic[] = [...TAXONOMY].sort(
  byDisplayLabelOtherLast,
);

export function findTopicByKey(key: string): ProblemReportTopic | undefined {
  return PROBLEM_REPORT_TOPICS.find((topic) => topic.key === key);
}

export function findTopicByLinearLabel(linearLabel: string): ProblemReportTopic | undefined {
  return PROBLEM_REPORT_TOPICS.find((topic) => topic.linearLabel === linearLabel);
}
